import React from 'react';
import {
	ALIGN_LEFT,
	ALIGN_CENTER,
	ALIGN_RIGHT
} from '@techchange/pm-specs/src/specs/nodes/paragraph.js';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import { useIntl } from 'react-intl';

import { messages } from '../../../../../../_shared/i18n/messages';

import { ToolbarButton } from '../../../Toolbar';
import getActiveTextAlignment from '../../utils/getActiveTextAlignment';
import { toggleAlignment } from '../commands';

const propTypesGeneric = {

	/**
	 * The id of the DOM element where the editor will be injected.
	 */
	editorDomId: PropTypes.string.isRequired,

	/*
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * The provided toolbar props to adhere to accessibility (a11y)
	 * guidelines for button toolbars.
	 *
	 * See here: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar
	 */
	a11yToolbarProps: PropTypes.shape({

		/**
		 * The tab index value for the button. Only the button with
		 * current focus should have tab index value of 0. All others
		 * should have value of -1.
		 */
		tabIndex: PropTypes.oneOf(['0', '-1']).isRequired,

		/**
		 * Callback function to handle keypress events on the button.
		 */
		onKeyDown: PropTypes.func.isRequired,

	}),

};

const propTypesSpecific = {

	/**
	 * The button alignment value.
	 */
	buttonAlignment: PropTypes.oneOf([
		ALIGN_LEFT,
		ALIGN_CENTER,
		ALIGN_RIGHT,
	]).isRequired,

	/**
	 * The button title text.
	 */
	buttonTitle: PropTypes.string.isRequired,

	...propTypesGeneric

};

const AlignButton = (props) => {

	const {
		buttonAlignment,
		buttonTitle,
		editorDomId,
		editorView,
		a11yToolbarProps = {},
	} = props;

	const activeAlignments = getActiveTextAlignment(editorView.state);

	let activeAlignment;

	if (activeAlignments.length === 1) {
		activeAlignment = activeAlignments[0];
	}

	const handleOnToggle = (updatedAlignment) => {

		const { state, dispatch } = editorView;

		toggleAlignment({ align: updatedAlignment })(state, dispatch);

	};

	return (
		<ToolbarButton
			id={`${editorDomId}-toolbar-btn-align-${buttonAlignment}`}
			icon={`fas fa-align-${buttonAlignment}`}
			active={activeAlignment === buttonAlignment}
			disabled={false}
			title={buttonTitle}
			onClick={() => handleOnToggle(buttonAlignment)}
			{...a11yToolbarProps} />
	);


};

const AlignButtonLeft = (props) => {

	const intl = useIntl();

	return (
		<AlignButton
			buttonAlignment={ALIGN_LEFT}
			buttonTitle={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_ALIGN_LEFT_BUTTON_TITLE)}
			{...props} />
	);

};

const AlignButtonCenter = (props) => {

	const intl = useIntl();

	return (
		<AlignButton
			buttonAlignment={ALIGN_CENTER}
			buttonTitle={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_ALIGN_CENTER_BUTTON_TITLE)}
			{...props} />
	);

};

const AlignButtonRight = (props) => {

	const intl = useIntl();

	return (
		<AlignButton
			buttonAlignment={ALIGN_RIGHT}
			buttonTitle={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_ALIGN_RIGHT_BUTTON_TITLE)}
			{...props} />
	);

};

AlignButtonLeft.propTypes = propTypesGeneric;
AlignButtonCenter.propTypes = propTypesGeneric;
AlignButtonRight.propTypes = propTypesGeneric;

AlignButton.propTypes = propTypesSpecific;

export {
	AlignButtonLeft,
	AlignButtonCenter,
	AlignButtonRight,
};
